import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Slide from "@mui/material/Slide";
import Snackbar, { SnackbarCloseReason } from "@mui/material/Snackbar";
import TextField from "@mui/material/TextField";
import clsx from "clsx";
import React, { useCallback, useState } from "react";
import { MdCheckCircle, MdError } from "react-icons/md";
import { classes, StyledButton, StyledForm } from "./styles";
import { FormData, SnackbarContent } from "./types";

const encode = (data: any) => {
  return Object.keys(data)
    .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
    .join("&");
};

const INITIAL_VALUES: FormData = {
  email: "",
  firstName: "",
  lastName: "",
  message: "",
  phone: "",
  structure: "",
  "bot-field": "",
};

const SUCCESS_SNACKBAR: SnackbarContent = {
  icon: MdCheckCircle,
  message: "Votre message a bien été envoyé !",
  success: true,
};

const ERROR_SNACKBAR: SnackbarContent = {
  icon: MdError,
  message:
    "Une erreur est survenue, votre message n'a pas été envoyé, veuillez réessayer.",
  success: false,
};

const Form = () => {
  const [formData, setFormData] = useState<FormData>(INITIAL_VALUES);
  const [snackbar, setSnackbar] = useState<SnackbarContent | null>(null);

  const validateForm = (data: FormData) => {
    const requiredFields = Object.keys(data).filter(
      (k) => !["bot-field", "message"].includes(k)
    );
    return requiredFields.every((field) => data[field]);
  };

  const handleChange = (e: any) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    const form = e.target;

    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({
          "form-name": form.getAttribute("name"),
          ...formData,
        }),
      });

      if (response.status) {
        if (response.status < 200 || response.status >= 300) throw response;
        else setSnackbar(SUCCESS_SNACKBAR);
      }
    } catch (error) {
      setSnackbar(ERROR_SNACKBAR);
    }
  };

  const handleSnackbarClose =
    (success?: boolean) =>
    (_event: Event | React.SyntheticEvent, reason?: SnackbarCloseReason) => {
      if (success === undefined) return;
      if (reason === "clickaway") return;
      success && setFormData(INITIAL_VALUES);
      setSnackbar(null);
    };

  const renderIcon = useCallback((item: SnackbarContent) => {
    const { icon: Icon } = item;
    return <Icon fontSize="inherit" />;
  }, []);

  return (
    <React.Fragment>
      <StyledForm
        name="contact"
        method="post"
        data-netlify="true"
        data-netlify-honeypot="bot-field"
        onSubmit={handleSubmit}
      >
        {/* Netlify form submission needs */}
        <input type="hidden" name="form-name" value="contact" />
        <p className={classes.honeypot} aria-hidden="true">
          <label>
            Don’t fill this out:{" "}
            <input
              name="bot-field"
              tabIndex={-1}
              autoComplete="off"
              onChange={handleChange}
              value={formData["bot-field"]}
            />
          </label>
        </p>

        <TextField
          name="structure"
          label="Nom de la structure"
          required
          className={classes.gutters}
          onChange={handleChange}
          value={formData["structure"]}
        />
        <TextField
          name="lastName"
          label="Nom"
          required
          className={classes.gutters}
          onChange={handleChange}
          value={formData["lastName"]}
        />
        <TextField
          name="firstName"
          label="Prénom"
          required
          className={classes.gutters}
          onChange={handleChange}
          value={formData["firstName"]}
        />
        <TextField
          name="phone"
          label="Téléphone"
          required
          className={classes.gutters}
          onChange={handleChange}
          value={formData["phone"]}
        />
        <TextField
          name="email"
          label="E-mail"
          required
          className={classes.gutters}
          onChange={handleChange}
          value={formData["email"]}
        />
        <TextField
          name="message"
          label="Message"
          multiline
          minRows={10}
          className={classes.gutters}
          onChange={handleChange}
          value={formData["message"]}
        />
        <StyledButton
          type="submit"
          aria-label="Envoyer"
          variant="contained"
          size="large"
          disabled={!validateForm(formData)}
        >
          Envoyer
        </StyledButton>
        <Snackbar
          open={Boolean(snackbar)}
          autoHideDuration={5000}
          onClose={handleSnackbarClose(snackbar?.success)}
          TransitionComponent={Slide}
          classes={{ root: classes.snackbarRoot }}
        >
          {snackbar ? (
            <Paper
              className={clsx(classes.snackbarPaper, {
                [classes.snackbarError]: !snackbar.success,
                [classes.snackbarSuccess]: snackbar.success,
              })}
            >
              <div className={classes.icon}>{renderIcon(snackbar)}</div>
              <div>{snackbar.message}</div>
            </Paper>
          ) : (
            <div></div>
          )}
        </Snackbar>
      </StyledForm>
    </React.Fragment>
  );
};

export default Form;
